/**
 * FindGaps.js
 * Layout order: header → tabs → feature preview → inputs → button → results
 */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress, alpha, useTheme,
  Tabs, Tab, IconButton, Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Compare as CompareIcon,
  TextFields as TextIcon,
  TrendingUp as TrendIcon,
  Analytics as ScoreIcon,
  Tag as TagIcon,
  Search as SearchIcon,
  AutoAwesome as AiIcon,
  EditNote as RewriteIcon,
  Bookmark as BookmarkIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  AccessTime as ClockIcon,
} from '@mui/icons-material';
import GapsInput from './gaps/GapsInput';
import GapsResults from './gaps/GapsResults';
import { gapsApi } from '../services/api';
import PageAlert from './PageAlert';
import { Skeleton } from '@mui/material';

// ── What you'll get — compact feature strip ───────────────────────────────────
const FEATURES = [
  { icon: <ScoreIcon sx={{ fontSize: 13 }} />,  color: '#6366f1', label: 'Score Comparison' },
  { icon: <TrendIcon sx={{ fontSize: 13 }} />,  color: '#10b981', label: 'Engagement Signals' },
  { icon: <TagIcon sx={{ fontSize: 13 }} />,    color: '#8b5cf6', label: 'Missing Keywords' },
  { icon: <SearchIcon sx={{ fontSize: 13 }} />, color: '#f59e0b', label: 'Topic Gaps' },
  { icon: <AiIcon sx={{ fontSize: 13 }} />,     color: '#ec4899', label: 'AI Recommendations' },
  { icon: <RewriteIcon sx={{ fontSize: 13 }} />,color: '#06b6d4', label: 'Post Rewrites' },
];

function FeatureStrip() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 3 }}>
      <Typography variant="caption" sx={{
        color: 'text.disabled', fontWeight: 700, fontSize: '0.67rem',
        textTransform: 'uppercase', letterSpacing: 0.6,
        display: 'flex', alignItems: 'center', mr: 0.5,
      }}>
        You'll get:
      </Typography>
      {FEATURES.map(f => (
        <Box key={f.label} sx={{
          display: 'flex', alignItems: 'center', gap: 0.6,
          px: 1.1, py: 0.4, borderRadius: '7px',
          bgcolor: alpha(f.color, isDark ? 0.1 : 0.07),
          border: `1px solid ${alpha(f.color, isDark ? 0.2 : 0.15)}`,
        }}>
          <Box sx={{ color: f.color, display: 'flex', alignItems: 'center' }}>{f.icon}</Box>
          <Typography variant="caption" sx={{ fontWeight: 700, color: f.color, fontSize: '0.71rem' }}>
            {f.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// ── Compact comparison bar (after first analysis) ─────────────────────────────
function CompactComparisonBar({ yourContent, compContent, compareMode, platform, loading, onEdit, onReanalyse }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const yourPreview = yourContent.length > 90 ? yourContent.slice(0, 90) + '…' : yourContent;
  const compPreview = compContent?.length > 90 ? compContent.slice(0, 90) + '…' : (compContent || '');
  const yourIsUrl = /^https?:\/\//i.test(yourContent.trim());

  const modeLabels = { competitor: 'vs Competitor', self_best: 'vs Your Best', niche_benchmark: 'vs Niche Benchmark' };

  return (
    <Paper elevation={0} sx={{
      p: 2, mb: 3, borderRadius: '12px',
      background: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '7px', flexShrink: 0,
          bgcolor: alpha('#6366f1', isDark ? 0.2 : 0.1), color: '#6366f1',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <TextIcon sx={{ fontSize: 14 }} />
        </Box>
        <Typography variant="body2" sx={{
          color: 'text.secondary', fontSize: '0.78rem',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {yourIsUrl ? yourContent : yourPreview}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
        <Chip label={modeLabels[compareMode] || 'vs Competitor'} size="small" sx={{
          fontWeight: 700, fontSize: '0.65rem', height: 22, borderRadius: '6px',
          bgcolor: alpha('#6366f1', 0.1), color: '#6366f1',
        }} />
        {platform && (
          <Chip label={platform} size="small" sx={{
            fontWeight: 700, fontSize: '0.65rem', height: 22, borderRadius: '6px',
            bgcolor: alpha('#10b981', 0.1), color: '#10b981',
          }} />
        )}
      </Box>

      {compareMode === 'competitor' && compPreview && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: '7px', flexShrink: 0,
            bgcolor: alpha('#10b981', isDark ? 0.2 : 0.1), color: '#10b981',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <TrendIcon sx={{ fontSize: 14 }} />
          </Box>
          <Typography variant="body2" sx={{
            color: 'text.secondary', fontSize: '0.78rem',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {compPreview}
          </Typography>
        </Box>
      )}

      <Button
        size="small" variant="outlined" startIcon={<EditIcon sx={{ fontSize: 13 }} />}
        onClick={onEdit}
        sx={{ borderRadius: '8px', fontSize: '0.76rem', fontWeight: 700, py: 0.5, px: 1.5, flexShrink: 0 }}
      >
        Edit
      </Button>
      <Button
        size="small" variant="contained"
        startIcon={loading ? <CircularProgress size={12} color="inherit" /> : <CompareIcon sx={{ fontSize: 13 }} />}
        onClick={onReanalyse} disabled={loading}
        sx={{
          borderRadius: '8px', fontSize: '0.76rem', fontWeight: 700, py: 0.5, px: 1.5, flexShrink: 0,
          background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
          '&:disabled': { background: alpha('#6366f1', 0.3), color: 'rgba(255,255,255,0.5)' },
        }}
      >
        {loading ? 'Analysing…' : 'Re-analyse'}
      </Button>
    </Paper>
  );
}

// ── History / Saved panel ─────────────────────────────────────────────────────
function HistoryPanel({ isPro }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [saved, setSaved] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    gapsApi.getSaved()
      .then(d => setSaved(d.saved_analyses || []))
      .catch(() => {})
      .finally(() => setLoadingSaved(false));
    gapsApi.getHistory()
      .then(d => setHistory(d.history || []))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, []);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await gapsApi.deleteSaved(id);
      setSaved(prev => prev.filter(s => s.id !== id));
    } catch (_) {}
    setDeletingId(null);
  };

  const card = {
    p: 2, borderRadius: '10px',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
  };

  const modeLabel = (m) => ({ competitor: 'vs Competitor', self_best: 'vs Your Best', niche_benchmark: 'vs Niche' }[m] || m);

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts._seconds ? ts._seconds * 1000 : ts);
    if (isNaN(d)) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <Box>
      {/* Saved Analyses */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BookmarkIcon sx={{ fontSize: 18, color: '#6366f1' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Saved Analyses</Typography>
          {!isPro && (
            <Chip label="5 max on Free" size="small" sx={{ ml: 'auto', height: 20, fontSize: '0.67rem', bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b' }} />
          )}
        </Box>

        {loadingSaved ? (
          [1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={64} sx={{ borderRadius: '10px', mb: 1 }} />)
        ) : saved.length === 0 ? (
          <Paper elevation={0} sx={{ ...card, textAlign: 'center', py: 4 }}>
            <BookmarkIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">No saved analyses yet</Typography>
            <Typography variant="caption" color="text.disabled">Run an analysis and click Save Analysis</Typography>
          </Paper>
        ) : (
          saved.map(item => (
            <Paper key={item.id} elevation={0} sx={{ ...card, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: '0.84rem', fontWeight: 600, mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.your_content_preview || 'Analysis'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                  {item.compare_mode && (
                    <Chip label={modeLabel(item.compare_mode)} size="small" sx={{ height: 18, fontSize: '0.63rem', bgcolor: alpha('#6366f1', 0.08), color: '#6366f1' }} />
                  )}
                  {item.platform && (
                    <Chip label={item.platform} size="small" sx={{ height: 18, fontSize: '0.63rem', bgcolor: alpha('#10b981', 0.08), color: '#10b981' }} />
                  )}
                  {item.analysis_summary?.your_overall != null && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                      Score: {item.analysis_summary.your_overall}
                    </Typography>
                  )}
                  {item.saved_at && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem', ml: 'auto' }}>
                      {formatDate(item.saved_at)}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Tooltip title="Delete">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(item.id)}
                    disabled={deletingId === item.id}
                    sx={{ color: 'text.disabled', '&:hover': { color: '#ef4444' } }}
                    aria-label="Delete saved analysis"
                  >
                    {deletingId === item.id ? <CircularProgress size={14} /> : <DeleteIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            </Paper>
          ))
        )}
      </Box>

      {/* Recent Runs */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <HistoryIcon sx={{ fontSize: 18, color: '#8b5cf6' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Recent Runs</Typography>
          {!isPro && (
            <Chip label="Last 5 on Free" size="small" sx={{ ml: 'auto', height: 20, fontSize: '0.67rem', bgcolor: alpha('#8b5cf6', 0.1), color: '#8b5cf6' }} />
          )}
        </Box>

        {loadingHistory ? (
          [1, 2, 3].map(i => <Skeleton key={i} variant="rectangular" height={50} sx={{ borderRadius: '10px', mb: 1 }} />)
        ) : history.length === 0 ? (
          <Paper elevation={0} sx={{ ...card, textAlign: 'center', py: 4 }}>
            <ClockIcon sx={{ fontSize: 28, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">No history yet</Typography>
          </Paper>
        ) : (
          history.map((item, i) => (
            <Paper key={i} elevation={0} sx={{ ...card, mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: '8px', flexShrink: 0,
                bgcolor: alpha('#8b5cf6', isDark ? 0.2 : 0.1),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#8b5cf6', fontSize: '0.82rem' }}>
                  {item.your_score ?? '—'}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                  {item.compare_mode && (
                    <Chip label={modeLabel(item.compare_mode)} size="small" sx={{ height: 18, fontSize: '0.63rem', bgcolor: alpha('#6366f1', 0.08), color: '#6366f1' }} />
                  )}
                  {item.platform && (
                    <Chip label={item.platform} size="small" sx={{ height: 18, fontSize: '0.63rem', bgcolor: alpha('#10b981', 0.08), color: '#10b981' }} />
                  )}
                  {item.personalized && (
                    <Chip label="Personalized" size="small" sx={{ height: 18, fontSize: '0.63rem', bgcolor: alpha('#ec4899', 0.08), color: '#ec4899' }} />
                  )}
                </Box>
                {item.gap_score != null && (
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                    Gap score: {item.gap_score}
                  </Typography>
                )}
              </Box>
              {item.generated_at && (
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem', flexShrink: 0 }}>
                  {formatDate(item.generated_at)}
                </Typography>
              )}
            </Paper>
          ))
        )}
      </Box>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
const FindGaps = React.memo(({ onFindGaps, loading, gaps, error, isPro = false }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [activeTab, setActiveTab] = useState(0);
  const [yourContent, setYourContent] = useState('');
  const [compContent, setCompContent] = useState('');
  const [platform, setPlatform] = useState(null);
  const [compareMode, setCompareMode] = useState('competitor');
  const [localError, setLocalError] = useState('');
  const [urlFetchAvailable, setUrlFetch] = useState(false);
  const [editing, setEditing] = useState(true);

  useEffect(() => {
    gapsApi.health().then(h => setUrlFetch(h.url_fetch));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!yourContent.trim()) { setLocalError('Please enter your content or a URL'); return; }
    if (compareMode === 'competitor' && !compContent.trim()) {
      setLocalError('Please enter the competitor content or a URL');
      return;
    }
    setLocalError('');
    setEditing(false);
    onFindGaps(yourContent, compareMode === 'competitor' ? compContent : '', { platform, compareMode });
  }, [yourContent, compContent, compareMode, platform, onFindGaps]);

  const handleReanalyse = useCallback(() => {
    if (!yourContent.trim()) return;
    onFindGaps(yourContent, compareMode === 'competitor' ? compContent : '', { platform, compareMode });
  }, [yourContent, compContent, compareMode, platform, onFindGaps]);

  const handleEdit = useCallback(() => setEditing(true), []);

  const isValid = useMemo(() => {
    if (loading) return false;
    if (compareMode !== 'competitor') return !!yourContent.trim();
    return !!(yourContent.trim() && compContent.trim());
  }, [yourContent, compContent, compareMode, loading]);

  const card = useMemo(() => ({
    p: { xs: 2.5, md: 3 },
    borderRadius: '12px',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.05)',
  }), [theme, isDark]);

  const hasResult = !!(gaps && gaps.scores);

  if (loading && !hasResult) {
    return (
      <Box>
        <Box sx={{ mb: 2 }}>
          <Skeleton variant="text" width={220} height={52} sx={{ mb: 0.5 }} />
          <Skeleton variant="text" width={380} height={24} />
        </Box>
        <Skeleton variant="rectangular" height={56} sx={{ borderRadius: '12px', mb: 3 }} />
        <Skeleton variant="rectangular" height={130} sx={{ borderRadius: '14px', mb: 2.5 }} />
        <Box sx={{ display: 'flex', gap: 2.5, mb: 2.5 }}>
          <Skeleton variant="rectangular" height={240} sx={{ borderRadius: '12px', flex: 5 }} />
          <Skeleton variant="rectangular" height={240} sx={{ borderRadius: '12px', flex: 7 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2.5, mb: 2.5 }}>
          <Skeleton variant="rectangular" height={160} sx={{ borderRadius: '12px', flex: 7 }} />
          <Skeleton variant="rectangular" height={160} sx={{ borderRadius: '12px', flex: 5 }} />
        </Box>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 0.75 }}>
          Gap Analysis
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          Compare your content against a competitor to find untapped growth opportunities
        </Typography>
      </Box>

      {/* ── Tabs ── */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 3, minHeight: 36,
          '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 700, fontSize: '0.84rem' },
        }}
      >
        <Tab label="Analyze" icon={<CompareIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
        <Tab label="History" icon={<HistoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
      </Tabs>

      {activeTab === 1 ? (
        <HistoryPanel isPro={isPro} />
      ) : (
        <>
          {editing && <FeatureStrip />}

          <PageAlert error={error || localError} onClose={() => setLocalError('')} />

          {editing ? (
            <GapsInput
              yourContent={yourContent}
              onYourChange={e => { setYourContent(e.target.value); setLocalError(''); }}
              compContent={compContent}
              onCompChange={e => { setCompContent(e.target.value); setLocalError(''); }}
              onSubmit={handleSubmit}
              loading={loading}
              isValid={isValid}
              urlFetchAvailable={urlFetchAvailable}
              platform={platform}
              onPlatformChange={setPlatform}
              compareMode={compareMode}
              onCompareModeChange={setCompareMode}
              isPro={isPro}
            />
          ) : (
            <CompactComparisonBar
              yourContent={yourContent}
              compContent={compContent}
              compareMode={compareMode}
              platform={platform}
              loading={loading}
              onEdit={handleEdit}
              onReanalyse={handleReanalyse}
            />
          )}

          {hasResult && (
            <Box sx={{ mt: editing ? 4 : 0 }}>
              <GapsResults result={gaps} card={card} isPro={isPro} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
});

export default FindGaps;
